import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, AlertTriangle, Mail, Phone, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const CONTACT_EMAIL = "contact@misyou.in";

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Important Disclaimer</h1>
          <p className="text-muted-foreground">Please read this carefully before using MISYOU</p>
        </div>

        {/* Critical Warning Box */}
        <Card className="shadow-lg border-red-200 bg-red-50 mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-red-800 flex items-center gap-2 mb-4">
              <AlertTriangle className="w-6 h-6" />
              CRITICAL: NOT A GOVERNMENT SERVICE
            </h2>
            <p className="text-red-700 text-lg">
              MISYOU is an <strong>INDEPENDENT, PRIVATE platform</strong>. We are <strong>NOT</strong>:
            </p>
            <ul className="text-red-700 mt-3 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                A government agency or department
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                A law enforcement authority or police service
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                An official investigation body
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Affiliated with any police department or government portal
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Emergency Contact Box */}
        <Card className="shadow-lg border-amber-200 bg-amber-50 mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-amber-800 flex items-center gap-2 mb-4">
              <Phone className="w-6 h-6" />
              ALWAYS CONTACT POLICE FIRST
            </h2>
            <p className="text-amber-700 mb-4">
              If someone is missing, your <strong>FIRST and IMMEDIATE step</strong> should be to contact the police:
            </p>
            
            <p className="font-semibold text-amber-800 mb-2">Government Emergency Helplines (India):</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="font-bold text-amber-800">Police Emergency</p>
                <p className="text-2xl font-bold text-amber-600">100</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="font-bold text-amber-800">Women Helpline</p>
                <p className="text-2xl font-bold text-amber-600">181</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="font-bold text-amber-800">Child Helpline (CHILDLINE)</p>
                <p className="text-2xl font-bold text-amber-600">1098</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="font-bold text-amber-800">Missing Persons</p>
                <p className="text-sm font-medium text-amber-600">Local Police Station / File FIR</p>
              </div>
            </div>
            
            <p className="font-semibold text-amber-800 mb-2">MISYOU Customer Support:</p>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                <p className="text-amber-700"><strong>Phone:</strong> <span className="text-amber-600 font-bold">+91 81464 99739</span></p>
                <p className="text-amber-700"><strong>Email:</strong> <a href="mailto:contact@misyou.in" className="text-amber-600 font-bold underline">contact@misyou.in</a></p>
              </div>
            </div>
            
            <p className="text-amber-700 mt-4 font-medium">
              MISYOU is a <strong>SUPPLEMENTARY tool</strong> to aid community awareness. It is NOT a replacement for official police reporting.
            </p>
          </CardContent>
        </Card>

        {/* Main Disclaimer Content */}
        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">

            <h2 className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-slate-600" />
              1. No Verification of Submissions
            </h2>
            <ul>
              <li>All information on this platform is <strong>user-generated</strong></li>
              <li>We do <strong>NOT independently verify</strong> the accuracy of reports</li>
              <li>We do <strong>NOT verify</strong> the identity of reporters</li>
              <li>False, misleading, or outdated information may exist on this platform</li>
              <li>Users should exercise caution and verify information through official channels</li>
            </ul>

            <h2>2. No Guarantee of Results</h2>
            <p>We make <strong>ABSOLUTELY NO PROMISES OR GUARANTEES</strong> that:</p>
            <ul>
              <li>Any missing person will be found through this platform</li>
              <li>Reports will receive any specific level of visibility or response</li>
              <li>The community will engage with or share your report</li>
              <li>Any particular outcome will be achieved</li>
            </ul>
            <p>Success depends entirely on factors beyond our control, including community participation, accuracy of provided information, timing, and circumstances.</p>

            <h2>3. User-Generated Content Liability</h2>
            <p>MISYOU is <strong>NOT responsible</strong> for:</p>
            <ul>
              <li>Accuracy, completeness, or truthfulness of user-submitted information</li>
              <li>Authenticity or legitimacy of uploaded photos</li>
              <li>Consequences arising from false or misleading reports</li>
              <li>Misuse of information by third parties</li>
              <li>Emotional distress caused by viewing content on this platform</li>
              <li>Actions taken by users based on information found here</li>
              <li>Privacy violations by users who misuse the platform</li>
            </ul>

            <h2>4. Limitation of Liability</h2>
            <p>
              Under <strong>NO circumstances</strong> shall MISYOU, its owners, directors, employees, partners, or affiliates be liable for:
            </p>
            <ul>
              <li>Any direct, indirect, incidental, special, or consequential damages</li>
              <li>Loss of data, profits, or business opportunities</li>
              <li>Personal injury or emotional harm</li>
              <li>Any damages arising from the use or inability to use this app</li>
              <li>Security breaches or data loss beyond our reasonable control</li>
            </ul>
            <p>
              <strong>Maximum liability is strictly limited</strong> to the amount paid by the user to MISYOU in the 12 months preceding any claim, if any.
            </p>

            <h2>5. Photo Consent & Usage</h2>
            <p>By viewing photos on this app, you agree <strong>NOT</strong> to:</p>
            <ul>
              <li>Misuse images for any illegal, harmful, or unauthorized purpose</li>
              <li>Harass, stalk, or threaten individuals shown in photos</li>
              <li>Share images outside the legitimate context of finding missing persons</li>
              <li>Use images for commercial purposes</li>
              <li>Create fake or misleading content using these images</li>
            </ul>

            <h2>6. Legal Compliance</h2>
            <p>All users must comply with applicable laws including but not limited to:</p>
            <ul>
              <li>Indian Penal Code (IPC)</li>
              <li>Information Technology Act, 2000</li>
              <li>Protection of Children from Sexual Offences Act (POCSO), 2012</li>
              <li>Right to Privacy as recognized by the Supreme Court of India</li>
              <li>Any applicable state or local laws regarding missing persons</li>
            </ul>

            <h2>7. Third-Party Content & Links</h2>
            <p>
              This app may contain links to third-party websites or services. We are not responsible for the content, 
              privacy practices, or availability of these external sites. Access them at your own risk.
            </p>

            <h2>8. Service Availability</h2>
            <p>We do not guarantee:</p>
            <ul>
              <li>Uninterrupted or error-free service</li>
              <li>That the app will be available at all times</li>
              <li>That defects will be corrected promptly</li>
              <li>That the app is free from viruses or harmful components</li>
            </ul>

            <h2>9. Modifications</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of this app at any time without prior notice. 
              We are not liable for any modification, suspension, or discontinuation of the service.
            </p>

            <div className="bg-slate-100 rounded-lg p-6 mt-8">
              <h2 className="mt-0">10. Acknowledgment</h2>
              <p className="text-lg font-medium">
                By using this app, you confirm that you have <strong>READ, UNDERSTOOD, and AGREE</strong> to this Disclaimer, 
                our <Link to="/privacy" className="text-primary">Privacy Policy</Link>, and 
                our <Link to="/terms" className="text-primary"> Terms & Conditions</Link>.
              </p>
              <p className="mb-0">
                If you do not agree with any part of this Disclaimer, please <strong>DO NOT use this app</strong>.
              </p>
            </div>

            <h2>11. Contact Us</h2>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-primary" />
                <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
              <p className="mb-2"><strong>Phone:</strong> +91 81464 99739</p>
              <p className="mb-2"><strong>Business Name:</strong> MISYOU</p>
              <p><strong>Jurisdiction:</strong> Bathinda, Punjab, India</p>
            </div>

            <p className="text-center text-muted-foreground mt-8">
              <strong>Effective Date:</strong> 17th February 2026
            </p>

          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/privacy">
            <Button variant="outline">Privacy Policy</Button>
          </Link>
          <Link to="/terms">
            <Button variant="outline">Terms & Conditions</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
